import { memo } from 'react';
import Button from '@amzn/awsui-components-core/button';

import RumFeedback from './RumFeedback';
import FreeTextFeedbackModal from './FreeTextFeedbackModal';
import { FeedbackComments } from './RumFeedbackViewModel';
import styles from './ThumbsFeedback.module.scss';

export type ThumbsValue = 'POSITIVE' | 'NEGATIVE';

const FEEDBACK_PROMPT_VALUES: readonly ThumbsValue[] = ['NEGATIVE'];

export interface ThumbsFeedbackProps {
  /** RUM event type string passed to `RumClient.recordEvent()`. */
  readonly eventType: string;
  /** Label shown before the thumbs icons. */
  readonly label: string;
  /**
   * Compose the RUM event payload when the user clicks thumbs-up/down or
   * submits follow-up feedback.
   *
   * - Initial click: `composeEvent('POSITIVE')` or `composeEvent('NEGATIVE')`
   * - Follow-up submission: `composeEvent('NEGATIVE', 'user comments')`
   *
   * Return the RUM event payload to record. The component enriches it with
   * `userId` before calling `RumClient.recordEvent`.
   */
  readonly composeEvent: (value: ThumbsValue, feedback?: string) => Record<string, unknown>;
  /**
   * Which rating values should prompt the user for follow-up feedback.
   * Default: `['NEGATIVE']` — shows the feedback modal on thumbs-down.
   * Pass an empty array to disable the modal entirely.
   */
  readonly promptFeedbackFor?: readonly ThumbsValue[];
}

/**
 * Thumbs up/down feedback component that emits CloudWatch RUM events.
 *
 * Shows a label with blue thumbs-up and thumbs-down icons. On click, the
 * component records a RUM event via the caller-provided `composeEvent`
 * callback. On thumbs-down (when `collectNegativeFeedback` is true), a
 * modal allows the user to submit free-form comments, which triggers
 * another `composeEvent` call with the feedback included.
 *
 * ## Design decisions
 *
 * - **Fire-and-forget RUM events.** This component is event-based, not
 *   state-based. Each click emits a CloudWatch RUM event; no backend
 *   persistence occurs. The consumer's data pipeline must handle
 *   deduplication and conflict resolution (e.g. "latest wins" in the
 *   Data Lake).
 *
 * - **No toggle.** Clicking the same button again does NOT retract the
 *   rating. The user can only change their selection by clicking the other
 *   option. The consumer treats the most recent event as the final choice.
 *
 * - **Unified `composeEvent` callback.** A single callback handles both the
 *   initial rating and optional follow-up feedback, so adding feedback
 *   collection to positive ratings in the future requires no signature
 *   change.
 *
 * - **Extensible `FeedbackComments` type.** Today the modal collects
 *   free-form text (`string`). The type is parameterised so future variants
 *   can submit structured form data without breaking existing consumers.
 *
 * @example
 * ```tsx
 * import { ThumbsFeedback } from '../common/rum-feedback';
 *
 * <ThumbsFeedback
 *   eventType="scan_feedback"
 *   label="Helpful scan results?"
 *   composeEvent={(value, feedback) => ({
 *     scanType: 'qualityCheck',
 *     contentId,
 *     rating: value,
 *     ...(feedback != null && { feedback }),
 *   })}
 * />
 * ```
 */
const ThumbsFeedback = memo(function ThumbsFeedback({
  eventType,
  composeEvent,
  label,
  promptFeedbackFor = FEEDBACK_PROMPT_VALUES,
}: ThumbsFeedbackProps) {
  return (
    <RumFeedback<ThumbsValue, string>
      eventType={eventType}
      composeEvent={composeEvent}
      feedbackPromptValues={promptFeedbackFor}
      label={label}
    >
      {(vm) => (
        <>
          {/* ButtonGroup does not expose the compact icon size or item gap required here. */}
          <span className={styles.buttonGroup}>
            <Button
              variant="link"
              iconName={vm.currentValue === 'POSITIVE' ? 'thumbs-up-filled' : 'thumbs-up'}
              ariaLabel="Positive"
              disabled={vm.isModalVisible}
              onClick={() => vm.select('POSITIVE')}
            />
            <Button
              variant="link"
              iconName={vm.currentValue === 'NEGATIVE' ? 'thumbs-down-filled' : 'thumbs-down'}
              ariaLabel="Negative"
              disabled={vm.isModalVisible}
              onClick={() => vm.select('NEGATIVE')}
            />
          </span>
          <FreeTextFeedbackModal
            visible={vm.isModalVisible}
            onSubmit={vm.submitFeedback}
            onDismiss={vm.dismissModal}
          />
        </>
      )}
    </RumFeedback>
  );
});

export default ThumbsFeedback;
